import { SelectStateContext } from 'app/components/forms/SelectState/constants';
import Option from 'app/components/forms/SelectState/Option';
import SelectWrapper from 'app/components/forms/SelectState/SelectWrapper';
import { getFirstIndexOfClosestFuzzyMatch } from 'app/utils/filtering';
import { clamp } from 'app/utils/numbers';
import { List } from 'immutable';
import React, { ReactNode, useEffect, useReducer } from 'react';

interface IChildProps {
  isOpen: boolean;
  focusedId: string | undefined;
  Option: typeof Option;
  SelectWrapper: typeof SelectWrapper;
}

interface ISelectStateProps<T extends string = string> {
  children: (props: IChildProps) => ReactNode;
  optionIds: List<T>;
  optionLabels?: List<string>;
  selectedId?: T; // undefined means no value is selected
  onSelect?: (id: T) => void; // Gets passed the corresponding ID from `optionIds`
  resetQueryTimeoutMs?: number; // Mainly for unit tests
}

// Declare state for the reducer.
interface IState<T extends string = string> {
  focusedId?: T;
  focusWrapperTrigger: number;
  isOpen: boolean;
  query: string;
}

// Declare actions for the reducer.
interface IClose {
  focusWrapper: boolean;
  type: 'close';
}

interface IFocusDelta {
  delta: 1 | -1;
  type: 'focusDelta';
}

interface IOpen {
  type: 'open';
}

interface IResetQuery {
  type: 'resetQuery';
}

interface ISetFocused<T extends string = string> {
  id: T;
  type: 'setFocused';
}

interface ISetQuery {
  query: string;
  type: 'setQuery';
}

type StateAction<T extends string = string> = IClose | IFocusDelta | IOpen | IResetQuery | ISetFocused<T> | ISetQuery;

/**
 * Utility component for providing all of the behavior of a select menu, but without any styling.
 * Handles things like opening/selecting on space/enter, navigating with arrow keys, focusing, etc.
 *
 * Example usage:
 *
 * ```
 * const optionIds = Immutable.List([...]);
 *
 * <SelectState
 *   optionIds={optionIds}
 *   optionLabels={optionIds.map(getNameForOption)}
 *   selectedId={...}
 *   onSelect={...}
 * >{({
 *   isOpen, focusedId, SelectWrapper, Option,
 * }) =>
 *   <SelectWrapper>{(selectWrapperElementProps) => (
 *     <button className={...} {...selectWrapperElementProps}>
 *
 *     {isOpen && (
 *       <Overlay>
 *         {optionIds.map(id =>
 *           <Option id={id} className={...}>{(optionElementProps) => (
 *             <button {...optionElementProps}>
 *               {getNameForOption(id)}
 *             </button>
 *           )}
 *           </Option>
 *         )}
 *       </Overlay>
 *     )}
 *   </SelectWrapper>
 * }</SelectState>
 * ```
 */
const SelectState = <T extends string= string>({
  children,
  onSelect,
  optionIds,
  optionLabels,
  resetQueryTimeoutMs = 2000,
  selectedId,
}: ISelectStateProps<T>) => {
  const [{
    focusedId = optionIds.first(),
    focusWrapperTrigger,
    isOpen,
    query,
  }, dispatch] = useReducer((state: IState<T>, action: StateAction<T>) => {
    if (action.type === 'resetQuery' && state.query !== '') {
      return { ...state, query: '' };
    } else if (action.type === 'close' && state.isOpen) {
      return {
        ...state,
        focusedId: undefined,
        // focusWrapperTrigger is used to tell the <SelectWrapper>, via the context, to focus itself
        // when closed in certain situations.
        focusWrapperTrigger: action.focusWrapper ? state.focusWrapperTrigger + 1 : state.focusWrapperTrigger,
        isOpen: false,
        query: '',
      };
    } else if (action.type === 'open' && !state.isOpen) {
      return {
        ...state,
        focusedId: selectedId,
        isOpen: true,
        query: '',
      };
    } else if (action.type === 'focusDelta') {
      const currentFocusedIndex = state.focusedId ? optionIds.indexOf(state.focusedId) : 0;
      const nextFocusedIndex = clamp(0, currentFocusedIndex + action.delta, optionIds.size - 1);
      const nextFocusedId = optionIds.get(nextFocusedIndex);

      return {
        ...state,
        focusedId: nextFocusedId,
        query: '',
      };
    } else if (action.type === 'setFocused') {
      return {
        ...state,
        focusedId: action.id,
        query: '',
      };
    } else if (action.type === 'setQuery' && action.query) {
      // Focus first item that matches the new query.
      // Find the index of the first fuzzy-filtering match.
      const matchIndex = getFirstIndexOfClosestFuzzyMatch({
        filter: action.query,
        list: optionLabels || optionIds,
        getItemText: (label) => label,
      });

      const newFocusedId = matchIndex >= 0 ? optionIds.get(matchIndex) : state.focusedId;

      return {
        ...state,
        focusedId: newFocusedId,
        isOpen: true, // If the user starts typing while the menu was closed, automatically open it.
        query: action.query,
      };
    } else {
      return state;
    }
  }, {
    focusedId: undefined,
    focusWrapperTrigger: 0,
    isOpen: false,
    query: '',
  });

  // Debounce the effect of the query being set to empty whenever the search query
  // changes.
  useEffect(() => {
    if (query !== '') {
      const resetQueryTimeoutId = setTimeout(() => {
        dispatch({ type: 'resetQuery' });
      }, resetQueryTimeoutMs);

      return () => {
        clearTimeout(resetQueryTimeoutId);
      };
    }
  }, [query, resetQueryTimeoutMs]);

  const select = (id: string) => {
    if (onSelect) {
      onSelect(id as T);
    }

    dispatch({ focusWrapper: true, type: 'close' });
  };

  return (
    <SelectStateContext.Provider
      value={{
        close: (focusWrapper: boolean) => dispatch({ focusWrapper, type: 'close' }),
        focusedId,
        focusNext: () => dispatch({ delta: 1, type: 'focusDelta' }),
        focusPrevious: () => dispatch({ delta: -1, type: 'focusDelta' }),
        focusWrapperTrigger,
        isOpen,
        open: () => dispatch({ type: 'open' }),
        optionIds,
        query,
        select,
        setFocusedId: (id: string) => dispatch({ id: id as T, type: 'setFocused' }),
        setQuery: (q: string) => dispatch({ query: q, type: 'setQuery' }),
      }}
    >
      {children({
        focusedId,
        isOpen,
        Option,
        SelectWrapper,
      })}
    </SelectStateContext.Provider>
  );
};

export default SelectState;
